import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Chip,
  Button,
  IconButton,
  TextField,
  Tooltip,
  Snackbar,
  Alert,
  Stack,
  Card,
  Typography,
  Divider,
  CircularProgress,
} from "@mui/material";
import {
  Close as CloseIcon,
  Email as EmailIcon,
  Business as BusinessIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Description,
  Refresh,
  Warning as WarningIcon,
  Info as InfoIcon,
} from "@mui/icons-material";

import { reportHeaderSx, reportPaperSx, reportFooterSx } from "./reportModalStyles";

import MDBox from "shared/components/MDBox";
import MDTypography from "shared/components/MDTypography";

// Subcomponente para el diálogo de confirmación
const ConfirmationDialog = ({
  open,
  onClose,
  onConfirm,
  action,
  email,
  comercioNombre,
  loading,
}) => {
  const getDialogConfig = () => {
    switch (action) {
      case "update":
        return {
          title: "Confirmar Actualización de Correo",
          message: "Se actualizará el destinatario de este reporte.",
          confirmText: "Actualizar",
          icon: <EditIcon sx={{ color: "#155e66" }} />,
          color: "primary",
        };
      case "add":
        return {
          title: "Agregar Nuevo Correo",
          message: "Se agregará este destinatario a los correos del reporte.",
          confirmText: "Agregar",
          icon: <AddIcon sx={{ color: "#155e66" }} />,
          color: "primary",
        };
      case "delete":
        return {
          title: "Eliminar Correo",
          message: "Se retirará este destinatario de los próximos envíos del reporte.",
          confirmText: "Eliminar",
          icon: <WarningIcon color="error" />,
          color: "error",
        };
      default:
        return {
          title: "Confirmar Acción",
          message: "¿Está seguro de realizar esta acción?",
          confirmText: "Confirmar",
          icon: <InfoIcon color="info" />,
          color: "primary",
        };
    }
  };

  const config = getDialogConfig();

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      aria-labelledby="report-email-confirmation-title"
      BackdropProps={{ sx: { bgcolor: "rgba(15,23,42,0.28)" } }}
      fullWidth
      PaperProps={{ sx: { ...reportPaperSx, maxWidth: 480 } }}
    >
      <DialogTitle id="report-email-confirmation-title" sx={reportHeaderSx}>
        <Box display="flex" alignItems="center" gap={1.25}>
          {config.icon}
          <Typography component="span" sx={{ fontSize: 16, fontWeight: 700 }}>
            {config.title}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ px: 2.5, pt: "20px !important", pb: 2.5 }}>
        <Typography variant="body2" color="text.primary" sx={{ mb: 2 }}>
          {config.message}
        </Typography>
        <Box
          sx={{
            p: 1.5,
            border: 1,
            borderColor: "divider",
            borderRadius: 2,
            bgcolor: "background.default",
          }}
        >
          <Typography variant="caption" color="text.secondary" display="block">
            Comercio
          </Typography>
          <Typography variant="body2" color="text.primary" fontWeight={600}>
            {comercioNombre}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            Correo
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <EmailIcon sx={{ fontSize: 18, color: "#155e66" }} />
            <Typography
              variant="body2"
              color="text.primary"
              sx={{ overflowWrap: "anywhere", minWidth: 0 }}
            >
              {email || "Sin dirección"}
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={reportFooterSx}>
        <Button
          onClick={onClose}
          disabled={loading}
          variant="outlined"
          sx={{
            color: "text.secondary",
            borderColor: "divider",
            textTransform: "none",
            fontWeight: "medium",
          }}
        >
          Cancelar
        </Button>
        <Button
          onClick={onConfirm}
          disabled={loading}
          variant="contained"
          color={config.color}
          startIcon={action === "delete" ? <DeleteIcon /> : undefined}
          sx={{
            textTransform: "none",
            fontWeight: "medium",
            px: 3,
            ...(action === "delete" && {
              "&&": { bgcolor: "#b91c1c", color: "#fff", border: "1px solid #b91c1c" },
              "&&:hover": { bgcolor: "#991b1b", borderColor: "#991b1b" },
              "&&.Mui-disabled": {
                bgcolor: "#e2e8f0",
                color: "#64748b",
                borderColor: "#cbd5e1",
              },
              "& .MuiSvgIcon-root": { color: "inherit" },
            }),
          }}
        >
          {loading ? "Guardando…" : config.confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ConfirmationDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  action: PropTypes.oneOf(["update", "add", "delete"]),
  email: PropTypes.string,
  comercioNombre: PropTypes.string,
  loading: PropTypes.bool,
};

// Componente principal del modal
const EmailsModalContent = ({
  open,
  onClose,
  report,
  onUpdateEmail,
  onAddNewEmail,
  onDeleteEmail,
  onRefresh,
}) => {
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingEmail, setEditingEmail] = useState("");
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [confirmationDialog, setConfirmationDialog] = useState({
    open: false,
    action: null,
    index: null,
    email: "",
  });
  const [localEmails, setLocalEmails] = useState([]);
  const [localStats, setLocalStats] = useState({
    totalCorreos: 0,
    correosValidos: 0,
  });
  const [loading, setLoading] = useState(false);

  // Efecto para sincronizar datos locales cuando cambia el report
  useEffect(() => {
    if (report) {
      const emails = report.paramData?.emails || [];
      setLocalEmails([...emails]);

      const validEmails = emails.filter((email) => email.Email && email.Email.trim() !== "").length;
      setLocalStats({
        totalCorreos: emails.length,
        correosValidos: validEmails,
      });
    }
  }, [report]);

  if (!report) return null;

  const comercioNombre = `${report.comercio} (${report.comercioId})`;

  const handleEditClick = (index, currentEmail) => {
    setEditingIndex(index);
    setEditingEmail(currentEmail || "");
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditingEmail("");
  };

  const openConfirmationDialog = (action, index, email) => {
    setConfirmationDialog({
      open: true,
      action,
      index,
      email,
    });
  };

  const closeConfirmationDialog = () => {
    setConfirmationDialog({
      open: false,
      action: null,
      index: null,
      email: "",
    });
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleConfirmAction = async () => {
    const { action, index, email } = confirmationDialog;
    setLoading(true);

    try {
      if (action === "update" && index !== null) {
        if (!validateEmail(email)) {
          setNotification({
            open: true,
            message: "Por favor ingrese un correo electrónico válido",
            severity: "error",
          });
          closeConfirmationDialog();
          setLoading(false);
          return;
        }

        // Actualizar inmediatamente en el estado local
        const updatedEmails = [...localEmails];
        updatedEmails[index] = {
          ...updatedEmails[index],
          Email: email,
        };
        setLocalEmails(updatedEmails);

        // Actualizar estadísticas locales
        const validEmails = updatedEmails.filter((e) => e.Email && e.Email.trim() !== "").length;
        setLocalStats({
          totalCorreos: updatedEmails.length,
          correosValidos: validEmails,
        });

        // Llamar a la función de actualización
        await onUpdateEmail(report.originalReportId, report.paramIndex, index, email);

        setNotification({
          open: true,
          message: "Correo actualizado exitosamente",
          severity: "success",
        });

        // Limpiar estado de edición
        setEditingIndex(null);
        setEditingEmail("");
      } else if (action === "add") {
        if (!validateEmail(email)) {
          setNotification({
            open: true,
            message: "Por favor ingrese un correo electrónico válido",
            severity: "error",
          });
          closeConfirmationDialog();
          setLoading(false);
          return;
        }

        const newEmailData = {
          Name: comercioNombre,
          Email: email,
        };

        // Agregar inmediatamente en el estado local
        const updatedEmails = [...localEmails, newEmailData];
        setLocalEmails(updatedEmails);

        // Actualizar estadísticas locales
        const validEmails = updatedEmails.filter((e) => e.Email && e.Email.trim() !== "").length;
        setLocalStats({
          totalCorreos: updatedEmails.length,
          correosValidos: validEmails,
        });

        // Llamar a la función de agregar
        await onAddNewEmail(report.originalReportId, report.paramIndex, newEmailData);

        setNotification({
          open: true,
          message: "Correo agregado exitosamente",
          severity: "success",
        });

        // Limpiar campo de nuevo correo
        setEditingIndex(null);
        setEditingEmail("");
      } else if (action === "delete" && index !== null) {
        // Verificar si es el último email
        if (localEmails.length <= 1) {
          // Si es el último, solo vaciar el email
          const updatedEmails = [...localEmails];
          updatedEmails[index] = {
            ...updatedEmails[index],
            Email: "",
          };
          setLocalEmails(updatedEmails);

          setLocalStats({
            totalCorreos: updatedEmails.length,
            correosValidos: 0,
          });

          // Llamar a la función de actualización (para vaciar)
          await onUpdateEmail(report.originalReportId, report.paramIndex, index, "");

          setNotification({
            open: true,
            message: "Correo vaciado (no se puede eliminar el último correo)",
            severity: "info",
          });
        } else {
          // Eliminar del estado local
          const updatedEmails = localEmails.filter((_, i) => i !== index);
          setLocalEmails(updatedEmails);

          // Actualizar estadísticas locales
          const validEmails = updatedEmails.filter((e) => e.Email && e.Email.trim() !== "").length;
          setLocalStats({
            totalCorreos: updatedEmails.length,
            correosValidos: validEmails,
          });

          // Llamar a la función de eliminación
          await onDeleteEmail(report.originalReportId, report.paramIndex, index);

          setNotification({
            open: true,
            message: "Correo eliminado exitosamente",
            severity: "success",
          });
        }
      }

      closeConfirmationDialog();
    } catch (error) {
      // Revertir cambios locales en caso de error
      const originalEmails = report.paramData?.emails || [];
      setLocalEmails([...originalEmails]);

      const validEmails = originalEmails.filter((e) => e.Email && e.Email.trim() !== "").length;
      setLocalStats({
        totalCorreos: originalEmails.length,
        correosValidos: validEmails,
      });

      setNotification({
        open: true,
        message: `Error al ${
          action === "delete" ? "eliminar" : action === "add" ? "agregar" : "actualizar"
        } el correo`,
        severity: "error",
      });
      closeConfirmationDialog();
    } finally {
      setLoading(false);
    }
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  const handleRefreshClick = () => {
    if (onRefresh) {
      onRefresh();
      showRefreshNotification();
    }
  };

  const showRefreshNotification = () => {
    setNotification({
      open: true,
      message: "Actualizando datos...",
      severity: "info",
    });
  };

  // Función para manejar Enter en el campo de nuevo correo
  const handleNewEmailKeyPress = (e) => {
    if (e.key === "Enter" && editingEmail.trim()) {
      openConfirmationDialog("add", null, editingEmail);
    }
  };

  // Función para renderizar cada email
  const renderEmailItem = (email, index) => {
    const tieneEmail = email.Email && email.Email.trim() !== "";
    const isEditing = editingIndex === index;

    return (
      <Card key={index} variant="outlined" sx={{ borderRadius: 1.5 }}>
        <MDBox p={1.5}>
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            gap={1.5}
            flexWrap="wrap"
          >
            {/* Email y acciones */}
            <MDBox flex={1}>
              {isEditing ? (
                <TextField
                  fullWidth
                  size="small"
                  value={editingEmail}
                  onChange={(e) => setEditingEmail(e.target.value)}
                  placeholder="ejemplo@dominio.com"
                  InputProps={{
                    startAdornment: <EmailIcon sx={{ mr: 1, color: "text.secondary" }} />,
                  }}
                  onKeyPress={(e) => {
                    if (e.key === "Enter" && editingEmail.trim()) {
                      openConfirmationDialog("update", index, editingEmail);
                    }
                  }}
                />
              ) : (
                <MDBox display="flex" alignItems="center" gap={2}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 32,
                      height: 32,
                      flexShrink: 0,
                      borderRadius: "50%",
                      backgroundColor: tieneEmail ? "rgba(46,125,50,0.08)" : "grey.100",
                    }}
                  >
                    <EmailIcon
                      sx={{
                        color: tieneEmail ? "success.main" : "text.secondary",
                      }}
                    />
                  </Box>
                  <Box flex={1}>
                    <MDTypography
                      variant="body2"
                      color={tieneEmail ? "text.primary" : "text.secondary"}
                      sx={{
                        overflowWrap: "anywhere",
                        fontWeight: tieneEmail ? 600 : "normal",
                        fontStyle: tieneEmail ? "normal" : "italic",
                      }}
                    >
                      {tieneEmail ? email.Email : "No configurado"}
                    </MDTypography>
                    <MDTypography variant="caption" color="text.secondary">
                      {comercioNombre}
                    </MDTypography>
                  </Box>
                </MDBox>
              )}
            </MDBox>

            {/* Botones de acción */}
            <MDBox display="flex" gap={1} alignItems="center">
              {isEditing ? (
                <>
                  <Tooltip title="Guardar cambios">
                    <span>
                      <IconButton
                        size="small"
                        sx={{
                          backgroundColor: "success.main",
                          color: "white",
                          "&:hover": { backgroundColor: "success.dark" },
                        }}
                        aria-label="Guardar cambios"
                        onClick={() => openConfirmationDialog("update", index, editingEmail)}
                        disabled={!editingEmail.trim() || loading}
                      >
                        <SaveIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Cancelar edición">
                    <span>
                      <IconButton
                        size="small"
                        sx={{
                          border: 1,
                          borderColor: "divider",
                          color: "text.secondary",
                        }}
                        aria-label="Cancelar edición"
                        onClick={handleCancelEdit}
                        disabled={loading}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </>
              ) : (
                <>
                  {tieneEmail ? (
                    <Chip
                      icon={<CheckIcon />}
                      label="Válido"
                      color="success"
                      size="small"
                      variant="outlined"
                      sx={{ mr: 1 }}
                    />
                  ) : (
                    <Chip
                      icon={<CancelIcon />}
                      label="Sin email"
                      color="warning"
                      size="small"
                      variant="outlined"
                      sx={{ mr: 1 }}
                    />
                  )}
                  <Tooltip title="Editar correo">
                    <span>
                      <IconButton
                        size="small"
                        sx={{
                          border: 1,
                          borderColor: "divider",
                          color: "#155e66",
                        }}
                        aria-label="Editar correo"
                        onClick={() => handleEditClick(index, email.Email)}
                        disabled={loading}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  {localEmails.length > 1 && (
                    <Tooltip title="Eliminar correo">
                      <span>
                        <IconButton
                          size="small"
                          sx={{
                            border: 1,
                            borderColor: "divider",
                            color: "error.main",
                            "&:hover": { backgroundColor: "error.lighter" },
                          }}
                          aria-label="Eliminar correo"
                          onClick={() => openConfirmationDialog("delete", index, email.Email)}
                          disabled={loading}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </>
              )}
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>
    );
  };

  return (
    <>
      {/* Diálogo principal */}
      <Dialog
        open={open}
        onClose={loading ? undefined : onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: "hidden",
            ...reportPaperSx,
            maxWidth: 780,
            "& .MuiCard-root": { boxShadow: "none", border: "1px solid", borderColor: "divider" },
          },
        }}
      >
        {/* Encabezado */}
        <DialogTitle sx={reportHeaderSx}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            gap={1.5}
            flexWrap="wrap"
          >
            <Box display="flex" alignItems="center" gap={2}>
              <Box
                sx={{
                  bgcolor: "rgba(255,255,255,0.12)",
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <BusinessIcon sx={{ color: "white", fontSize: 24 }} />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight="medium">
                  Gestión de Correos
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {comercioNombre}
                </Typography>
              </Box>
            </Box>
            <Box display="flex" gap={1}>
              <Tooltip title="Actualizar datos">
                <span>
                  <IconButton
                    size="small"
                    sx={{
                      border: 1,
                      borderColor: "divider",
                      color: "text.secondary",
                    }}
                    aria-label="Actualizar destinatarios"
                    onClick={handleRefreshClick}
                    disabled={loading}
                  >
                    {loading ? <CircularProgress size={20} /> : <Refresh />}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Cerrar">
                <span>
                  <IconButton
                    onClick={onClose}
                    size="small"
                    sx={{
                      border: 1,
                      borderColor: "divider",
                      color: "text.secondary",
                    }}
                    disabled={loading}
                  >
                    <CloseIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ p: { xs: 2, sm: 2.5 }, bgcolor: "background.default" }}>
          {/* Información resumida */}
          <Card variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
            <Box p={2}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                gap={1.5}
                flexWrap="wrap"
              >
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    Reporte
                  </Typography>
                  <Chip
                    label={report.reporte}
                    size="small"
                    sx={{
                      color: "secondary.main",
                      borderColor: "secondary.main",
                      fontWeight: "medium",
                    }}
                    variant="outlined"
                    icon={<Description sx={{ color: "secondary.main" }} fontSize="small" />}
                  />
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    Estadísticas de Correos
                  </Typography>
                  <Chip
                    label={`${localStats.correosValidos} válidos / ${localStats.totalCorreos} total`}
                    size="small"
                    sx={{
                      backgroundColor:
                        localStats.correosValidos > 0 ? "success.main" : "transparent",
                      color: localStats.correosValidos > 0 ? "white" : "text.secondary",
                      borderColor: localStats.correosValidos > 0 ? "success.main" : "divider",
                      fontWeight: "medium",
                    }}
                    variant={localStats.correosValidos > 0 ? "filled" : "outlined"}
                    icon={
                      <EmailIcon
                        sx={{ color: localStats.correosValidos > 0 ? "white" : "text.secondary" }}
                        fontSize="small"
                      />
                    }
                  />
                </Box>
              </Box>
            </Box>
          </Card>

          {/* Sección: Correos existentes */}
          <Box mb={2}>
            <Box display="flex" alignItems="center" mb={2}>
              <EmailIcon sx={{ mr: 1.5, color: "#155e66" }} />
              <Typography variant="h6" fontWeight="medium">
                Correos Configurados
                <Typography component="span" variant="body2" color="text.secondary" ml={1}>
                  ({localEmails.length})
                </Typography>
              </Typography>
            </Box>

            {localEmails.length === 0 ? (
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <Box p={4} textAlign="center">
                  <EmailIcon sx={{ fontSize: 56, color: "grey.400", mb: 2 }} />
                  <Typography variant="h6" color="text.primary" gutterBottom>
                    No hay correos configurados
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Agregue un correo para este comercio utilizando el formulario inferior
                  </Typography>
                </Box>
              </Card>
            ) : (
              <Stack spacing={1.5}>
                {localEmails.map((email, index) => renderEmailItem(email, index))}
              </Stack>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Sección: Agregar nuevo correo */}
          <Box>
            <Box display="flex" alignItems="center" mb={2}>
              <AddIcon sx={{ mr: 1.5, color: "#155e66" }} />
              <Typography variant="h6" fontWeight="medium">
                Agregar Nuevo Correo
              </Typography>
            </Box>

            <Card variant="outlined" sx={{ borderRadius: 2, borderColor: "rgba(21,94,102,0.3)" }}>
              <Box p={2}>
                <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap" mb={1.5}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="ejemplo@dominio.com"
                    value={editingIndex === "new" ? editingEmail : ""}
                    onChange={(e) => {
                      setEditingIndex("new");
                      setEditingEmail(e.target.value);
                    }}
                    InputProps={{
                      startAdornment: <EmailIcon sx={{ mr: 1, color: "text.secondary" }} />,
                    }}
                    onKeyPress={handleNewEmailKeyPress}
                    sx={{ flex: "1 1 260px" }}
                    disabled={loading}
                  />
                  <Tooltip title="Agregar nuevo correo">
                    <span>
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => openConfirmationDialog("add", null, editingEmail)}
                        disabled={!editingEmail.trim() || editingIndex !== "new" || loading}
                        sx={{
                          textTransform: "none",
                          fontWeight: "medium",
                          minWidth: 120,
                        }}
                      >
                        Agregar
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Este correo se agregará a: {comercioNombre}
                </Typography>
              </Box>
            </Card>
          </Box>
        </DialogContent>

        <DialogActions sx={reportFooterSx}>
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              color: "text.primary",
              borderColor: "divider",
              textTransform: "none",
              fontWeight: "medium",
            }}
            disabled={loading}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de confirmación */}
      <ConfirmationDialog
        open={confirmationDialog.open}
        onClose={closeConfirmationDialog}
        onConfirm={handleConfirmAction}
        action={confirmationDialog.action}
        email={confirmationDialog.email}
        comercioNombre={comercioNombre}
        loading={loading}
      />

      {/* Notificaciones */}
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          variant="filled"
          sx={{ width: "100%", minWidth: 300 }}
          iconMapping={{
            info: <InfoIcon />,
            success: <CheckIcon />,
            warning: <WarningIcon />,
            error: <CancelIcon />,
          }}
        >
          <Typography variant="body2" fontWeight="medium">
            {notification.message}
          </Typography>
        </Alert>
      </Snackbar>
    </>
  );
};

// Cada apertura y cada concesión tienen un formulario independiente. Al cambiar
// de contexto también se descartan confirmaciones, avisos y respuestas pendientes.
const EmailsModal = (props) => {
  const { open, report } = props;
  if (!open || !report) return null;
  const scope = JSON.stringify([report.originalReportId, report.comercioId, report.paramIndex]);
  return <EmailsModalContent key={scope} {...props} />;
};

EmailsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  report: PropTypes.shape({
    originalReportId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    paramIndex: PropTypes.number,
    comercioId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    comercio: PropTypes.string,
    reporte: PropTypes.string,
    totalCorreos: PropTypes.number,
    correosValidos: PropTypes.number,
    paramData: PropTypes.shape({
      emails: PropTypes.arrayOf(
        PropTypes.shape({
          Email: PropTypes.string,
        })
      ),
    }),
  }),
  onUpdateEmail: PropTypes.func.isRequired,
  onAddNewEmail: PropTypes.func.isRequired,
  onDeleteEmail: PropTypes.func.isRequired,
  onRefresh: PropTypes.func,
};

EmailsModalContent.propTypes = EmailsModal.propTypes;

EmailsModal.defaultProps = {
  report: null,
  onRefresh: null,
};

export default EmailsModal;
